import { css } from "@linaria/core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import cx from "classnames";
import { CollapseDiagonalLine, ExpandDiagonalLine } from "./IconV2/react";
import ButtonIconV2 from "./ButtonIconV2";
import { spacings } from "./Theme";
import TooltipV2 from "./TooltipV2";
import t from "./i18n";
import { HIDE_ON_WIDGET_EXPORT } from "./ExportWidgetButton";
import { HIDE_ON_EXPORT } from "./lib/downloadPage";

export const FullViewportButtonMarker = "FullViewportButtonTarget";
export const FullViewportButtonMarkerActive = "FullViewportButtonTarget--active";
export const FullViewportButtonMarkerInactive = "FullViewportButtonTarget--inactive";
export const FullViewportButtonMarkerAnimating = "FullViewportButtonTarget--animating";

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
css`
  :global() {
    @media screen {
      @keyframes expand {
        from {
          top: var(--top);
          left: var(--left);
          bottom: var(--bottom);
          right: var(--right);
        }
        to {
          top: 12px;
          left: 12px;
          bottom: 12px;
          right: 12px;
        }
      }

      :is(.${FullViewportButtonMarkerActive}, .${FullViewportButtonMarkerInactive}) {
        position: fixed;
        width: auto;
        height: auto;
        z-index: 8;
      }

      .${FullViewportButtonMarkerActive} {
        pointer-events: auto;
        overflow: hidden;
      }

      .${FullViewportButtonMarkerAnimating} {
        pointer-events: none;
        will-change: top, left, bottom, right;

        svg {
          contain: paint;
          shape-rendering: optimizeSpeed;
          text-rendering: optimizeSpeed;

          path {
            clip-path: none;
          }
        }
      }

      .${FullViewportButtonMarker}--active {
        animation: expand 350ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
      }

      .${FullViewportButtonMarker}--inactive {
        animation: expand 350ms cubic-bezier(0.4, 0, 0.2, 1) reverse forwards;
      }
    }
  }
`;

const FullViewportButton = css`
  box-sizing: border-box;
  width: ${spacings.xl};
  height: ${spacings.xl};
  @media print {
    display: none;
  }
  > * {
    pointer-events: none;
  }
`;

export function FullViewportTrigger({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  const [expanded, expand] = useState<boolean>(false);
  const dummy = useRef<HTMLDivElement>(document.createElement("div"));

  const expandHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!(e.target instanceof HTMLButtonElement)) return;

      const target = e.target?.closest(`.${FullViewportButtonMarker}`) as HTMLButtonElement;
      if (!target) return;

      if (!expanded) {
        const rect = target.getBoundingClientRect();

        target.style.setProperty("--top", `${Math.round(rect.top)}px`);
        target.style.setProperty("--bottom", `${Math.round(window.innerHeight - rect.bottom)}px`);
        target.style.setProperty(
          "--right",
          `${Math.round(document.body.clientWidth - rect.right)}px`,
        );
        target.style.setProperty("--left", `${Math.round(rect.left)}px`);

        dummy.current.style.width = `${rect.width}px`;
        dummy.current.style.height = `${rect.height}px`;
        document.body.style.overflowY = "hidden";
        document.body.style.pointerEvents = "none";
      }

      globalThis.requestAnimationFrame(() => {
        const nextAnimationStateClass = expanded
          ? FullViewportButtonMarkerInactive
          : FullViewportButtonMarkerActive;
        const currentAnimationStateClass = expanded
          ? FullViewportButtonMarkerActive
          : FullViewportButtonMarkerInactive;

        if (!expanded) target.insertAdjacentElement("afterend", dummy.current);

        target.classList.remove(currentAnimationStateClass);

        // hack:
        // trigger reflow, to allow the animation to run again either in the same direction or the opposite.
        // cannot rely on animation-play-state: revert;
        void target.offsetWidth;
        target.classList.add(nextAnimationStateClass);
        target.classList.add(FullViewportButtonMarkerAnimating);

        target.addEventListener(
          "animationend",
          () => {
            target.classList.remove(FullViewportButtonMarkerAnimating);
          },
          { passive: true, once: true },
        );

        if (expanded) {
          target.addEventListener(
            "animationend",
            () => {
              dummy.current.remove();
              globalThis.requestAnimationFrame(() => {
                target.classList.remove(nextAnimationStateClass);
                document.body.style.overflowY = "auto";
                document.body.style.pointerEvents = "initial";
              });
            },
            {
              passive: true,
              once: true,
            },
          );
        }

        expand(!expanded);
      });
    },
    [expanded],
  );

  const button = useRef<HTMLButtonElement>(null);
  const clearExpanded = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && expanded) button.current?.click();
    },
    [expanded],
  );
  useEffect(() => {
    window.addEventListener("keydown", clearExpanded);
    return () => window.removeEventListener("keydown", clearExpanded);
  }, [clearExpanded, expanded]);

  return (
    <TooltipV2
      openDelay={300}
      overlay={expanded ? t("chart.full_viewport.close") : t("chart.full_viewport.open")}
      closeOnOutsideClick
      closeDelay={0}
      hover
    >
      <ButtonIconV2
        ref={button}
        onClick={e => expandHandler(e)}
        className={cx(FullViewportButton, className, HIDE_ON_WIDGET_EXPORT, HIDE_ON_EXPORT)}
        style={style}
      >
        {expanded ? <CollapseDiagonalLine size="m" /> : <ExpandDiagonalLine size="m" />}
      </ButtonIconV2>
    </TooltipV2>
  );
}
